import {
  useJson, nf, plural, fmtDate, Chip, IdBadge, entityPath, victimPath, sectorPath, CATEGORY_BY_SLUG, Box, About, OnThisPage,
} from "./shared";
import { BarList } from "./Charts";
import { ReportCard } from "./Reports";
import { StixBar } from "./Stix";
import { OpRow, Credit } from "./CyberOps";

// Critical Infrastructure › ICS / SCADA (#/kb/ics): attacks on industrial control systems, from
// ATT&CK for ICS (its incidents, groups, malware and techniques), EuRepoC's operations against
// energy, water and industry, ransomware claims against energy and utility companies, CISA's ICS
// advisories and the exploited vulnerabilities in industrial products, and the SCADA rules.
// Server: icsHub() in knowledge.js, the hand-kept rules in ics.js.

const more = (ui, href, label) => <a href={href} style={{ fontSize: ui.fs(12), color: ui.B.accent, textTransform: "none", letterSpacing: 0, fontWeight: 600 }}>{label} →</a>;
const note = (ui, text) => <span style={{ textTransform: "none", letterSpacing: 0, fontWeight: 500 }}>{text}</span>;
const grid = (min) => ({ display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, ${min}px), 1fr))`, gap: 14, alignItems: "start" });
const dim = (ui, text) => <div style={{ fontSize: ui.fs(12), color: ui.B.textDim, lineHeight: 1.5 }}>{text}</div>;
const big = (ui, n, after) => <div style={{ fontSize: ui.fs(30), fontWeight: 900, color: ui.B.text, lineHeight: 1.1, marginBottom: 8 }}>{nf(n)}{after && <span style={{ fontSize: ui.fs(13), fontWeight: 600, color: ui.B.textDim }}> {after}</span>}</div>;
const year = (d) => (d ? String(d).slice(0, 4) : null);
// ATT&CK dates its campaigns to the month.
const month = (d) => { try { return new Date(d).toLocaleDateString("en", { month: "short", year: "numeric", timeZone: "UTC" }); } catch { return year(d); } };

function EntityChips({ items, ui, max = 10, none = "None in ATT&CK" }) {
  const { getConf, fs, B } = ui;
  if (!items.length) return <span style={{ fontSize: fs(12), color: B.textDim }}>{none}</span>;
  return (
    <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
      {items.slice(0, max).map(e => <a key={e.id} href={entityPath(e)} style={{ textDecoration: "none" }}><Chip ui={ui} color={getConf(e.type).color}>{e.name}</Chip></a>)}
      {items.length > max && <Chip ui={ui}>+{items.length - max} more</Chip>}
    </div>
  );
}

export function KbIcs({ ui }) {
  const { B, fs, getConf, TypeIcon } = ui;
  const { data, error, loading } = useJson("/api/kb/defense/ics");
  const cat = CATEGORY_BY_SLUG.ics;
  const heading = (
    <>
      <h2 style={{ fontSize: fs(22), fontWeight: 900, margin: "4px 0 6px", color: B.text }}>{cat.label}</h2>
      <div style={{ color: B.textMuted, fontSize: fs(13), margin: "0 0 16px", maxWidth: "110ch", lineHeight: 1.6 }}>
        Industrial control systems run power grids, water and wastewater plants, pipelines, chemical plants and factories: SCADA servers, the PLCs that drive pumps and breakers, and the safety systems meant to stop a disaster. An attack on them can cut power, poison water or break machines, which is why a handful of state groups have built malware just for them.
        <About ui={ui} inline>This page brings together the real incidents MITRE ATT&CK for ICS records, the groups and malware behind them and how they work, politically motivated operations against energy, water and industry, ransomware claims against energy and utility companies, CISA's advisories and the industrial products attackers are known to exploit, and the detection rules for industrial protocols.</About>
      </div>
    </>
  );
  if (loading) return <>{heading}<div style={{ color: B.textDim, fontSize: fs(13) }}>Loading…</div></>;
  if (error) return <>{heading}<div style={{ color: "#dc2626", fontSize: fs(12) }}>{error}</div></>;
  const d = data;
  const ops = d.operations;
  const rw = d.ransomware;
  const cell = { padding: "9px 12px", borderTop: `1px solid ${B.border}`, fontSize: fs(12), verticalAlign: "top" };
  const opsLink = `#/kb/cyber-ops?kind=all&cat=${encodeURIComponent(d.operationCats.join(","))}`;
  const ruleEngines = [["suricata", "Suricata (ET Open)"], ["snort", "Snort (Talos)"]];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div>
        {heading}
        <StixBar apiKey="ics" ui={ui} />
        <OnThisPage ui={ui} />
      </div>

      <div style={grid(240)}>
        <Box title="Incidents" ui={ui} right={note(ui, "in ATT&CK for ICS")}>
          {big(ui, d.incidents.length)}
          {dim(ui, d.incidents.length ? `From ${year(d.incidents[d.incidents.length - 1].firstSeen)} to ${year(d.incidents[0].firstSeen)}, below.` : "None loaded.")}
        </Box>
        <Box title="Groups" ui={ui} right={note(ui, "in ATT&CK for ICS")}>
          {big(ui, d.groups.length)}
          {dim(ui, `With ${plural(d.malware.length, "piece")} of malware and ${plural(d.techniquesTotal, "ICS technique")}${d.version ? ` (v${d.version})` : ""}.`)}
        </Box>
        <Box title="Cyber operations" jump="Operations" ui={ui} right={more(ui, opsLink, "All")}>
          {big(ui, ops.total, "since 2000")}
          {dim(ui, "Against energy, water, chemical and manufacturing targets, from EuRepoC and governments' statements.")}
        </Box>
        <Box title="Ransomware claims" jump="Claims" ui={ui}>
          {big(ui, rw.last365, "in 12 months")}
          {dim(ui, <>{nf(rw.total)} in all, against companies ransomware.live puts in "{rw.sector}". <a href={sectorPath(rw.sector)} style={{ color: B.accent, fontWeight: 600 }}>The sector →</a></>)}
        </Box>
      </div>

      <Box title="Real attacks on industrial systems" jump="Real attacks" ui={ui} right={note(ui, "MITRE ATT&CK for ICS's campaigns, newest first")}>
        {d.incidents.length ? (
          <div style={grid(360)}>
            {d.incidents.map(x => (
              <div key={x.id} style={{ border: `1px solid ${B.border}`, borderRadius: 10, padding: "12px 14px", minWidth: 0 }}>
                <div style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap", marginBottom: 4 }}>
                  <a href={entityPath(x)} style={{ fontSize: fs(14), fontWeight: 800, color: B.text, textDecoration: "none" }}>{x.name}</a>
                  <IdBadge id={x.attackId} ui={ui} />
                  <span style={{ fontSize: fs(12), color: B.textDim, marginLeft: "auto", whiteSpace: "nowrap" }}>{x.firstSeen ? month(x.firstSeen) : "Undated"}</span>
                </div>
                {x.snippet && <div style={{ fontSize: fs(12), color: B.textMuted, lineHeight: 1.55, marginBottom: 8, display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{x.snippet}</div>}
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "baseline" }}><span style={{ fontSize: fs(11), color: B.textDim, minWidth: 70 }}>Attributed to</span><EntityChips items={x.groups} ui={ui} none="No one, in ATT&CK" /></div>
                  {x.software.length > 0 && <div style={{ display: "flex", gap: 8, alignItems: "baseline" }}><span style={{ fontSize: fs(11), color: B.textDim, minWidth: 70 }}>Malware, tools</span><EntityChips items={x.software} ui={ui} /></div>}
                  <div style={{ fontSize: fs(11), color: B.textDim }}>{plural(x.techniques, "ICS technique")} recorded</div>
                </div>
              </div>
            ))}
          </div>
        ) : dim(ui, "None loaded.")}
      </Box>

      <div style={grid(420)}>
        <Box title="Who attacks industrial systems" jump="Who attacks" ui={ui} right={note(ui, "by how many ICS techniques ATT&CK records for each")}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 420 }}>
              <thead>
                <tr style={{ textAlign: "left", fontSize: fs(10), color: B.textDim, textTransform: "uppercase", letterSpacing: 1 }}>
                  <th style={{ padding: "9px 12px", fontWeight: 700 }}>Group</th>
                  <th style={{ padding: "9px 12px", fontWeight: 700, textAlign: "right" }}>ICS techniques</th>
                  <th style={{ padding: "9px 12px", fontWeight: 700 }}>Incidents and malware</th>
                </tr>
              </thead>
              <tbody>
                {d.groups.map(g => (
                  <tr key={g.id}>
                    <td style={{ ...cell, minWidth: 140 }}>
                      <a href={entityPath(g)} style={{ display: "inline-flex", gap: 6, alignItems: "center", color: B.text, fontWeight: 700, textDecoration: "none" }}><TypeIcon type={g.type} color={getConf(g.type).color} size={13} />{g.name}</a>
                      {g.aliases.length > 0 && <div style={{ fontSize: fs(11), color: B.textDim, marginTop: 2 }}>{g.aliases.join(", ")}</div>}
                    </td>
                    <td style={{ ...cell, textAlign: "right", color: B.text, fontWeight: 700 }}>{nf(g.techniques)}</td>
                    <td style={cell}><EntityChips items={[...g.incidents, ...g.software]} ui={ui} max={6} none="—" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Box>
        <Box title="Malware used against them" jump="Malware" ui={ui} right={note(ui, "by ICS techniques")}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 420 }}>
              <thead>
                <tr style={{ textAlign: "left", fontSize: fs(10), color: B.textDim, textTransform: "uppercase", letterSpacing: 1 }}>
                  <th style={{ padding: "9px 12px", fontWeight: 700 }}>Malware</th>
                  <th style={{ padding: "9px 12px", fontWeight: 700, textAlign: "right" }}>ICS techniques</th>
                  <th style={{ padding: "9px 12px", fontWeight: 700 }}>Used by</th>
                  <th style={{ padding: "9px 12px", fontWeight: 700, textAlign: "right" }}>Rules</th>
                </tr>
              </thead>
              <tbody>
                {d.malware.map(m => (
                  <tr key={m.id}>
                    <td style={{ ...cell, minWidth: 120 }}><a href={entityPath(m)} style={{ display: "inline-flex", gap: 6, alignItems: "center", color: B.text, fontWeight: 700, textDecoration: "none" }}><TypeIcon type={m.type} color={getConf(m.type).color} size={13} />{m.name}</a></td>
                    <td style={{ ...cell, textAlign: "right", color: B.text, fontWeight: 700 }}>{nf(m.techniques)}</td>
                    <td style={cell}><EntityChips items={[...m.incidents, ...m.groups]} ui={ui} max={4} none="—" /></td>
                    <td style={{ ...cell, textAlign: "right" }}>{m.rules ? <a href={`${entityPath(m)}?tab=rules`} style={{ color: B.accent }}>{nf(m.rules)}</a> : <span style={{ color: B.textDim }}>—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Box>
      </div>

      <Box title={`How they work: ATT&CK for ICS${d.version ? ` v${d.version}` : ""}`} jump="How they work" ui={ui} right={more(ui, "#/kb/frameworks/ics", "The matrix")}
        about="Each of the matrix's tactics, with the techniques these incidents, groups and malware use most. The last three are what sets industrial attacks apart: stopping the safety and alarm functions that would catch the problem (Inhibit Response Function), changing what the process does (Impair Process Control), and the damage that follows (Impact).">
        <div style={grid(220)}>
          {d.tactics.map(t => (
            <div key={t.shortname} style={{ border: `1px solid ${B.border}`, borderRadius: 10, padding: "10px 12px", minWidth: 0 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "baseline", marginBottom: 6 }}>
                <span style={{ fontSize: fs(13), fontWeight: 800, color: B.text }}>{t.name}</span>
                <span style={{ fontSize: fs(11), color: B.textDim, whiteSpace: "nowrap" }}>{plural(t.techniques, "technique")}</span>
              </div>
              {t.top.length ? t.top.map(x => (
                <div key={x.id} style={{ display: "flex", gap: 8, alignItems: "baseline", fontSize: fs(12), padding: "2px 0" }}>
                  <a href={entityPath(x)} style={{ color: B.textMuted, textDecoration: "none", flex: 1, minWidth: 0 }}>{x.name}</a>
                  <span style={{ color: B.textDim, whiteSpace: "nowrap" }} title={`Used by ${plural(x.users, "incident, group or malware", "incidents, groups or malware")}`}>{nf(x.users)}</span>
                </div>
              )) : dim(ui, "None recorded.")}
            </div>
          ))}
        </div>
      </Box>

      <div style={grid(420)}>
        <Box title="Cyber operations against energy, water and industry" jump="Operations by sector" ui={ui} right={more(ui, opsLink, `All ${nf(ops.total)}`)}
          about="Politically relevant incidents whose targets EuRepoC files under these sectors. Most hit the companies' office networks (data theft, DDoS, defacements), not the control systems themselves.">
          <BarList rows={ops.byCat.map(c => ({ label: c.key, value: c.count, href: `#/kb/cyber-ops?kind=all&cat=${encodeURIComponent(c.key)}` }))} ui={ui} unit="Incidents" keepOrder />
          {ops.byKind.length > 0 && <div style={{ fontSize: fs(12), color: B.textDim, margin: "8px 0 4px" }}>By who: {ops.byKind.map(k => `${k.label} ${nf(k.count)}`).join(" · ")}</div>}
          <div style={{ marginTop: 6 }}>{ops.items.slice(0, 6).map(x => <OpRow key={x.id} x={x} ui={ui} />)}</div>
          <div style={{ marginTop: 8 }}><Credit credit={ops.credit} ui={ui} /></div>
        </Box>
        <Box title="Ransomware claims against energy and utilities" jump="Ransomware claims" ui={ui} right={note(ui, "the claims are the criminals' own")}
          about="Leak-site claims, not confirmed breaches, and ransomware almost always hits the office network: a claim says nothing about the plant's control systems.">
          {rw.groups.length > 0 && <>
            <div style={{ fontSize: fs(11), color: B.textDim, marginBottom: 6 }}>Groups, last 12 months</div>
            <BarList rows={rw.groups.map(g => ({ label: g.label, value: g.count, href: g.link ? `#/kb/e/${encodeURIComponent(g.link)}` : undefined }))} ui={ui} unit="Claims" maxRows={6} />
          </>}
          {rw.recent.length ? (
            <div style={{ overflowX: "auto", marginTop: 10 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 420 }}>
                <thead>
                  <tr style={{ textAlign: "left", fontSize: fs(10), color: B.textDim, textTransform: "uppercase", letterSpacing: 1 }}>
                    <th style={{ padding: "9px 12px", fontWeight: 700 }}>Company (claimed)</th>
                    <th style={{ padding: "9px 12px", fontWeight: 700 }}>Group</th>
                    <th style={{ padding: "9px 12px", fontWeight: 700 }}>Country</th>
                    <th style={{ padding: "9px 12px", fontWeight: 700 }}>Claimed</th>
                  </tr>
                </thead>
                <tbody>
                  {rw.recent.map(r => (
                    <tr key={r.vid}>
                      <td style={{ ...cell, fontWeight: 600 }}><a href={victimPath(r.vid)} style={{ color: B.text, textDecoration: "none" }}>{r.title}</a></td>
                      <td style={cell}>{r.group.link ? <a href={`#/kb/e/${encodeURIComponent(r.group.link)}`} style={{ color: B.textMuted }}>{r.group.label}</a> : r.group.label}</td>
                      <td style={{ ...cell, color: B.textMuted }}>{r.countryName || "—"}</td>
                      <td style={{ ...cell, color: B.textMuted, whiteSpace: "nowrap" }}>{r.day ? fmtDate(r.day) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : dim(ui, rw.builtAt ? "No claims in this sector." : "Loading… victims arrive a minute or two after the server starts.")}
        </Box>
      </div>

      <div style={grid(420)}>
        <Box title="Exploited in the wild" ui={ui} right={note(ui, "CISA's KEV catalog, industrial vendors")}
          about="Vulnerabilities in industrial products that CISA has confirmed attackers use, picked out by the vendor's name (Siemens, Rockwell, Schneider Electric, Unitronics and other makers of control equipment).">
          {d.exploited.items.length ? d.exploited.items.map(v => (
            <div key={v.cve} style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap", padding: "7px 0", borderTop: `1px solid ${B.border}`, fontSize: fs(12) }}>
              <a href={entityPath(v)} style={{ color: B.text, fontWeight: 700, textDecoration: "none", whiteSpace: "nowrap" }}>{v.cve}</a>
              <span style={{ color: B.textMuted, flex: "1 1 220px", minWidth: 0 }}>{v.vendor} {v.product}</span>
              {v.ransomware && <Chip ui={ui} color="#dc2626">Used by ransomware</Chip>}
              <span style={{ color: B.textDim, whiteSpace: "nowrap" }}>added {fmtDate(v.added)}</span>
            </div>
          )) : dim(ui, d.exploited.builtAt ? "None from the industrial vendors kept here." : "Loading… the catalog arrives a few seconds after the server starts.")}
        </Box>
        <Box title="Detection rules for industrial protocols" jump="Detection rules" ui={ui} right={more(ui, `#/kb/rules?q=${d.rules.query}`, "All")}
          about="Network rules that watch industrial traffic (Modbus, IEC 104, Siemens S7) and attacks on SCADA software: Emerging Threats' SCADA rules and Talos's PROTOCOL-SCADA rules. The malware above has its own rules, in the Rules column.">
          {d.rules.total ? <BarList rows={ruleEngines.filter(([e]) => d.rules.byEngine[e]).map(([e, l]) => ({ label: l, value: d.rules.byEngine[e], href: `#/kb/${e}?q=${d.rules.query}` }))} ui={ui} unit="Rules" keepOrder /> : dim(ui, "Loading… the rule sets arrive several minutes after the server starts.")}
        </Box>
      </div>

      <Box title={`CISA ICS advisories (${nf(d.advisories.total)})`} jump="CISA advisories" ui={ui} right={more(ui, "#/kb/reports?source=cisa-ics", "All")}
        about="Vulnerabilities in industrial control systems and medical devices, published by CISA with the vendors: which products and versions, the CVEs, and what to do.">
        {d.advisories.items.length
          ? <div style={grid(300)}>{d.advisories.items.map(s => <ReportCard key={s.id} s={s} ui={ui} />)}</div>
          : dim(ui, "Loading… advisories arrive with the other reports, a few minutes after the server starts.")}
      </Box>

      <Box title={`Reports on attacks on industrial systems (${nf(d.reports.total)})`} jump="Reports" ui={ui} right={note(ui, "by title, summary and tags, or naming an ICS incident, malware or technique")}>
        {d.reports.items.length
          ? <div style={grid(300)}>{d.reports.items.map(s => <ReportCard key={s.id} s={s} ui={ui} />)}</div>
          : dim(ui, "None among the current reports.")}
      </Box>
    </div>
  );
}
